'use client';

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { nav } from '@/lib/marketing-content';
import { BrandLogo } from '@/components/brand-logo';
import { CornerTicks } from '@/components/marketing/site/hud';
import { MarketingNavLink } from '@/components/marketing/site/marketing-nav-link';
import { ThemeToggle } from '@/components/marketing/site/theme-toggle';

export function SiteHeader() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  function isActive(href: string) {
    if (href.startsWith('/#')) return pathname === '/';
    if (href === '/developers' || href.startsWith('/docs')) {
      return pathname === '/developers' || pathname.startsWith('/docs') || pathname.startsWith('/products/apis');
    }
    return pathname === href || pathname.startsWith(`${href}/`);
  }

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || open ? 'border-line/80 bg-ink/85 border-b backdrop-blur-xl' : 'border-b border-transparent'
      } ${scrolled ? 'sm:h-16' : ''}`}
    >
      <div className={`shell flex items-center justify-between gap-6 transition-all duration-300 ${scrolled ? 'h-14 sm:h-16' : 'h-16 sm:h-20'}`}>
        <Link to="/" className="flex items-center gap-2.5" onClick={() => setOpen(false)}>
          <BrandLogo variant="full" size={scrolled ? 'sm' : 'md'} priority />
        </Link>

        <nav className="hidden items-center gap-6 lg:flex xl:gap-8">
          {nav.map((item) => {
            const active = isActive(item.href);
            const className = `readout relative py-1 text-xs transition-colors xl:text-xs ${active ? 'text-accent' : 'text-muted hover:text-fg'}`;

            if (item.href.includes('#')) {
              return (
                <MarketingNavLink key={item.href} href={item.href} className={className}>
                  {active ? <span className="bg-accent absolute -top-1.5 left-1/2 h-0.5 w-3 -translate-x-1/2" /> : null}
                  {item.label}
                </MarketingNavLink>
              );
            }

            return (
              <Link key={item.href} to={item.href} className={className}>
                {active ? <span className="bg-accent absolute -top-1.5 left-1/2 h-0.5 w-3 -translate-x-1/2" /> : null}
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <ThemeToggle />
          <Link to="/login" className="readout text-muted hover:text-fg hidden px-2 py-2 transition-colors sm:inline-flex sm:px-3">
            Sign in
          </Link>
          <Link
            to="/register"
            className="notch-sm border-accent text-accent hover:bg-accent readout relative hidden border px-4 py-2.5 transition-colors hover:text-black sm:inline-flex sm:px-5 sm:py-2.5"
          >
            Start building
          </Link>
          <Link
            to="/register"
            className="notch-sm bg-accent readout inline-flex border border-transparent px-3 py-2 text-black transition-colors sm:hidden"
          >
            Sign up
          </Link>

          <button
            type="button"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="border-line bg-surface relative flex h-10 w-10 items-center justify-center border lg:hidden"
          >
            <CornerTicks className="border-accent/60" />
            <span className="relative block h-3 w-4">
              <span className={`bg-fg absolute left-0 block h-px w-4 transition-transform duration-300 ${open ? 'top-1.5 rotate-45' : 'top-0'}`} />
              <span className={`bg-fg absolute left-0 block h-px w-4 transition-transform duration-300 ${open ? 'top-1.5 -rotate-45' : 'top-3'}`} />
            </span>
          </button>
        </div>
      </div>

      <div className={`bg-ink/97 overflow-hidden backdrop-blur-xl transition-[max-height] duration-500 lg:hidden ${open ? 'max-h-svh' : 'max-h-0'}`}>
        <nav className="shell flex flex-col py-6">
          {nav.map((item) =>
            item.href.includes('#') ? (
              <MarketingNavLink
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                className="border-line/60 display hover:text-accent flex min-h-14 items-center justify-between border-b py-4 text-lg transition-colors sm:text-xl"
              >
                {item.label}
                <span className="text-faint readout">↗</span>
              </MarketingNavLink>
            ) : (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setOpen(false)}
                className="border-line/60 display hover:text-accent flex min-h-14 items-center justify-between border-b py-4 text-lg transition-colors sm:text-xl"
              >
                {item.label}
                <span className="text-faint readout">↗</span>
              </Link>
            ),
          )}
          <div className="mt-6 grid grid-cols-2 gap-3">
            <Link to="/login" onClick={() => setOpen(false)} className="notch-sm border-line readout border px-5 py-3.5 text-center">
              Sign in
            </Link>
            <Link to="/register" onClick={() => setOpen(false)} className="notch-sm bg-accent readout px-5 py-3.5 text-center text-black">
              Start building
            </Link>
          </div>
        </nav>
      </div>
    </header>
  );
}
